import translate from "@/i18n/translate";
import WidgetLoader from "../../common/elements/WidgetLoader";
import { useReducer } from "react";
import ViewCourseDetails from "../ViewCourseDetails";
import { sendRequest } from "../../../common/http";
import { StudentAssessmentProgressBySection } from "../../common/interfaces/Interface";
import RenderCourseAssessments from "./RenderCourseAssessments";

interface ProgramCurriculumEngagementProps {
  assessmentProgressByCourse: AssessmentProgressByCourse[];
  showLoader: boolean;
  termUuid: string;
  platformUuid: string | null;
}

interface AssessmentProgress {
  studentEnrolled: number | null;
  submissionRate: number | null;
  assessmentRate: number | null;
  actualCompletedAssessments: number | null;
  actualStudentSubmission: number | null;
  expectedCompletedAssessments: number | null;
  expectedStudentSubmission: number | null;
}
export interface AssessmentProgressByCourse {
  uuid: string;
  code: string;
  name: string;
  sectionCount: number;
  assessmentProgress: AssessmentProgress;
}

interface ProgramCurriculumEngagementState {
  isFlyoutOpen: boolean;
  courseCode: string;
  courseName: string;
  showTable: boolean;
  hierarchy: StudentAssessmentProgressBySection[] | null;
}
const initialState: ProgramCurriculumEngagementState = {
  isFlyoutOpen: false,
  showTable: false,
  courseCode: "",
  courseName: "",
  hierarchy: null
};
interface Action {
  type: string;
  isFlyoutOpen: boolean;
  courseCode: string;
  courseName: string;
  data: any;
}

const defaultAction: Action = {
  type: "",
  isFlyoutOpen: false,
  courseCode: "",
  courseName: "",
  data: {}
};

const reducer = (state: ProgramCurriculumEngagementState, action: Action) => {
  switch (action.type) {
    case "toggleFlyout":
      return { ...state, isFlyoutOpen: !state.isFlyoutOpen, hierarchy: null };

    case "CloseFlyout":
      return { ...state, isFlyoutOpen: false };

    case "setCourseDetails":
      return {
        ...state,
        showTable: true,
        hierarchy: action.data.studentAssessmentProgressBySection
      };

    case "setFlyoutData":
      return {
        ...state,
        courseCode: action.courseCode,
        courseName: action.courseName
      };
    default:
      return state;
  }
};

interface HeaderProps {
  assessmentProgressByCourseLength: number;
}

const ProgramCurriculumEngagement = ({
  assessmentProgressByCourse,
  showLoader,
  termUuid,
  platformUuid
}: ProgramCurriculumEngagementProps) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const setIsFlyoutOpen = () => {
    dispatch({
      ...defaultAction,
      type: "toggleFlyout"
    });
  };

  const CloseDrilldownView = () => {
    dispatch({
      ...defaultAction,
      type: "CloseFlyout"
    });
  };

  const openFlyoutCourseDetails = (
    assessmentProgressByCourse: AssessmentProgressByCourse
  ) => {
    const applyResult = (data: any) => {
      dispatch({
        ...defaultAction,
        type: "setCourseDetails",
        data: data
      });
    };

    let requestUri =
      "/sll/program_course_details?" +
      "term_platform_uuid=" +
      termUuid +
      "&course_platform_uuid=" +
      assessmentProgressByCourse.uuid +
      "&organization_platform_uuid=" +
      platformUuid;
    sendRequest({ uri: requestUri, camelize: true }, applyResult);

    dispatch({
      ...defaultAction,
      type: "setFlyoutData",
      courseCode: assessmentProgressByCourse.code,
      courseName: assessmentProgressByCourse.name
    });
    setIsFlyoutOpen();
  };
  return (
    <>
      <div className="wm-grid-row program-curriculum-engagement">
        <span
          className="sr-only faculty-loader-status"
          role="status"
          aria-live="assertive"
        ></span>
        <div className="grid-container wm-grid-col-12 wm-grid-col-md-10">
          <div className="paper-card paper-card-height-program">
            <Header
              assessmentProgressByCourseLength={
                assessmentProgressByCourse.length
              }
            />
            <div className="">
              {showLoader ? (
                <WidgetLoader
                  loaderContainerClasses="loader"
                  loaderClasses="content"
                />
              ) : (
                <RenderCourseAssessments
                  assessmentProgressByCourse={assessmentProgressByCourse}
                  openFlyoutCourseDetails={openFlyoutCourseDetails}
                />
              )}
            </div>
          </div>
        </div>
      </div>
            <ViewCourseDetails
              courseName={state.courseName}
              courseCode={state.courseCode}
              CloseDrilldownView={CloseDrilldownView}
              showTable={state.showTable}
              hierarchy={state?.hierarchy || []}
              open={state.isFlyoutOpen}
            ></ViewCourseDetails>
    </>
  );
};

const Header = ({ assessmentProgressByCourseLength }: HeaderProps) => {
  return (
    <div className="header">
      <div className="info">
        <h3 className="title">
          {translate("app.programCurriculumEngagement.widgetLabel")}
        </h3>
        <div className="flex-row -justified program-engagement-desc">
          <p className="description">
            {translate("app.programCurriculumEngagement.widgetDescription")}
          </p>
          {assessmentProgressByCourseLength > 0 && <Legends />}
        </div>
      </div>
    </div>
  );
};

const Legends = (): JSX.Element => {
  return (
    <div className="legend flex-row">
      <div className="completed flex-row">
        <span className="completed-box"></span>
        <span>{translate("app.drilldown.completed")} </span>
      </div>
      <div className="not-completed flex-row">
        <span className="not-completed-box"></span>
        <span>{translate("app.programCurriculumEngagement.notCompleted")}</span>
      </div>
    </div>
  );
};

export default ProgramCurriculumEngagement;
